import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Lock, User as UserIcon, Eye, EyeOff } from "lucide-react";
import { LOGO_URL } from "../constants";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const errFromApi = (e) => {
    try {
        const msg = e?.message || "";
        const idx = msg.indexOf("{");
        if (idx >= 0) {
            const j = JSON.parse(msg.slice(idx));
            if (typeof j.detail === "string") return j.detail;
            if (Array.isArray(j.detail)) return j.detail.map((x) => x.msg).join(" ");
        }
    } catch { /* ignore */ }
    return e?.message || "Something went wrong";
};

const redirectForRole = (user) => user?.role === "admin" ? "/admin" : "/dashboard";

export default function Login() {
    const [tab, setTab] = useState("login");
    return (
        <div style={{ minHeight: "100vh", background: "#0A0B0E", color: "#fff", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "0 24px", height: 64, display: "flex", alignItems: "center" }}>
                <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "#6b7280", textDecoration: "none" }} data-testid="back-home">
                    <ArrowLeft size={15} /> Back home
                </Link>
            </div>

            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 24px" }}>
                <div style={{ width: "100%", maxWidth: 420 }}>
                    <div style={{
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        borderRadius: 24, padding: 36
                    }}>
                        {/* Logo */}
                        <div style={{ textAlign: "center", marginBottom: 24 }}>
                            <div style={{ background: "#000", borderRadius: 16, padding: "10px 16px", display: "inline-flex", marginBottom: 20 }}>
                                <img src={LOGO_URL} alt="Blue Mountain Group" style={{ height: 40, width: "auto" }} />
                            </div>
                            <h1 style={{ fontSize: 26, fontWeight: 900, letterSpacing: "-0.03em", marginBottom: 6 }}>
                                {tab === "login" ? "Welcome operator." : tab === "register" ? "Join the operation." : "Reset your password."}
                            </h1>
                            <p style={{ fontSize: 13, color: "#6b7280" }}>
                                {tab === "login" && "Sign in to access your training and certificates."}
                                {tab === "register" && "Create an account in 30 seconds."}
                                {tab === "forgot" && "We'll send a reset link if the email is registered."}
                            </p>
                        </div>

                        {/* Tabs */}
                        {tab !== "forgot" && (
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, padding: 4, background: "rgba(0,0,0,0.3)", borderRadius: 99, marginBottom: 24 }}>
                                {[["login", "Sign in"], ["register", "Create account"]].map(([key, label]) => (
                                    <button
                                        key={key}
                                        data-testid={`tab-${key}`}
                                        onClick={() => setTab(key)}
                                        style={{
                                            padding: "9px 16px", borderRadius: 99,
                                            background: tab === key ? "#fff" : "transparent",
                                            color: tab === key ? "#0A0B0E" : "#6b7280",
                                            fontWeight: 700, fontSize: 13, border: "none",
                                            cursor: "pointer", transition: "all 0.2s"
                                        }}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        )}

                        {tab === "login" && <LoginForm onForgot={() => setTab("forgot")} />}
                        {tab === "register" && <RegisterForm />}
                        {tab === "forgot" && <ForgotForm onBack={() => setTab("login")} />}

                        {tab !== "forgot" && (
                            <>
                                <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: "#4b5563" }}>
                                    <span style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.06)" }} />
                                    or
                                    <span style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.06)" }} />
                                </div>
                                <GoogleButton />
                            </>
                        )}

                        <p style={{ marginTop: 20, fontSize: 11, color: "#4b5563", textAlign: "center" }}>
                            By continuing you agree to our Terms and Privacy Policy.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

function GoogleButton() {
    const handle = () => {
        const redirectUrl = window.location.origin + "/dashboard";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };
    return (
        <button
            onClick={handle}
            data-testid="google-login-btn"
            style={{
                width: "100%", display: "flex", alignItems: "center",
                justifyContent: "center", gap: 10,
                padding: "12px 20px", borderRadius: 99,
                border: "1px solid rgba(255,255,255,0.1)",
                background: "transparent", color: "#fff",
                fontSize: 13, fontWeight: 600, cursor: "pointer",
                transition: "background 0.2s"
            }}
        >
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                <path fill="#4285F4" d="M21.6 12.227c0-.709-.064-1.39-.182-2.046H12v3.873h5.382a4.6 4.6 0 0 1-1.996 3.018v2.51h3.232c1.892-1.742 2.982-4.305 2.982-7.355z"/>
                <path fill="#34A853" d="M12 22c2.7 0 4.964-.895 6.618-2.418L15.386 17.07c-.895.6-2.04.955-3.386.955-2.605 0-4.81-1.76-5.596-4.123H3.064v2.59A9.997 9.997 0 0 0 12 22z"/>
                <path fill="#FBBC05" d="M6.404 13.902a6.005 6.005 0 0 1 0-3.804V7.51H3.064a10.005 10.005 0 0 0 0 8.98l3.34-2.588z"/>
                <path fill="#EA4335" d="M12 5.977c1.468 0 2.786.505 3.823 1.496l2.868-2.868C16.96 2.99 14.696 2 12 2A9.997 9.997 0 0 0 3.064 7.51l3.34 2.588C7.19 7.736 9.395 5.977 12 5.977z"/>
            </svg>
            Continue with Google
        </button>
    );
}

const inputStyle = (focused) => ({
    width: "100%", boxSizing: "border-box",
    background: "rgba(0,0,0,0.3)",
    border: `1px solid ${focused ? "rgba(207,255,80,0.4)" : "rgba(255,255,255,0.08)"}`,
    borderRadius: 12, padding: "12px 16px 12px 42px",
    color: "#fff", fontSize: 14, outline: "none",
    transition: "border-color 0.2s"
});

const submitStyle = {
    width: "100%", padding: "13px 20px", borderRadius: 99,
    background: "#CFFF50", color: "#0A0B0E",
    fontWeight: 800, fontSize: 14, border: "none",
    cursor: "pointer", transition: "opacity 0.2s"
};

function Field({ icon: Icon, type, placeholder, value, onChange, testid, extra }) {
    const [focused, setFocused] = useState(false);
    return (
        <div style={{ position: "relative" }}>
            <Icon size={15} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#6b7280" }} />
            <input
                data-testid={testid}
                type={type}
                required
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                style={inputStyle(focused)}
            />
            {extra}
        </div>
    );
}

function LoginForm({ onForgot }) {
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);
    const [focused, setFocused] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const res = await api.post("/auth/login", { email, password });
            setUser(res.user);
            toast.success(`Welcome back, ${res.user.name}`);
            navigate(redirectForRole(res.user));
        } catch (err) {
            toast.error(errFromApi(err));
        }
        setBusy(false);
    };

    return (
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ position: "relative" }}>
                <Mail size={15} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#6b7280" }} />
                <input
                    data-testid="login-email"
                    type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    style={inputStyle(false)}
                />
            </div>
            <div style={{ position: "relative" }}>
                <Lock size={15} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#6b7280" }} />
                <input
                    data-testid="login-password"
                    type={show ? "text" : "password"} required value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    style={{ ...inputStyle(false), paddingRight: 44 }}
                />
                <button
                    type="button" onClick={() => setShow(!show)}
                    style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#6b7280", cursor: "pointer" }}
                >
                    {show ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
            </div>
            <div style={{ textAlign: "right" }}>
                <button
                    data-testid="forgot-link" type="button" onClick={onForgot}
                    style={{ fontSize: 12, color: "#6b7280", background: "none", border: "none", cursor: "pointer" }}
                >
                    Forgot password?
                </button>
            </div>
            <button data-testid="login-submit" type="submit" disabled={busy} style={{ ...submitStyle, opacity: busy ? 0.6 : 1 }}>
                {busy ? "Signing in…" : "Sign in"}
            </button>
        </form>
    );
}

function RegisterForm() {
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) return toast.error("Password must be at least 8 characters");
        setBusy(true);
        try {
            const res = await api.post("/auth/register", { email, password, name });
            setUser(res.user);
            toast.success(`Welcome, ${res.user.name}!`);
            navigate(redirectForRole(res.user));
        } catch (err) {
            toast.error(errFromApi(err));
        }
        setBusy(false);
    };

    return (
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ position: "relative" }}>
                <UserIcon size={15} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#6b7280" }} />
                <input data-testid="register-name" type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" style={inputStyle(false)} />
            </div>
            <div style={{ position: "relative" }}>
                <Mail size={15} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#6b7280" }} />
                <input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" style={inputStyle(false)} />
            </div>
            <div style={{ position: "relative" }}>
                <Lock size={15} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#6b7280" }} />
                <input data-testid="register-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (min 8 chars)" style={inputStyle(false)} />
            </div>
            <button data-testid="register-submit" type="submit" disabled={busy} style={{ ...submitStyle, opacity: busy ? 0.6 : 1 }}>
                {busy ? "Creating account…" : "Create account"}
            </button>
        </form>
    );
}

function ForgotForm({ onBack }) {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/auth/forgot-password", { email });
            toast.success("If that email is registered we've sent a reset link.");
            setEmail("");
            onBack();
        } catch {
            toast.error("Something went wrong");
        }
        setBusy(false);
    };

    return (
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ position: "relative" }}>
                <Mail size={15} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#6b7280" }} />
                <input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" style={inputStyle(false)} />
            </div>
            <button data-testid="forgot-submit" type="submit" disabled={busy} style={{ ...submitStyle, opacity: busy ? 0.6 : 1 }}>
                {busy ? "Sending…" : "Send reset link"}
            </button>
            <button type="button" onClick={onBack} style={{ fontSize: 12, color: "#6b7280", background: "none", border: "none", cursor: "pointer", marginTop: 4 }}>
                Back to sign in
            </button>
        </form>
    );
}
